import compose from 'lodash/flowRight';
import React from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Badge from '@material-ui/core/Badge';
import { ChatBubbleSharp, Event, Person, Settings, Description } from '@material-ui/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { colors } from '../themes/colors';
import { getCurrentUser } from '../selectors/userSelectors';
import { getHasUnread } from '../selectors/chatSelectors';

const drawerWidth = 85;

const styles = (theme) => ({
  sidebar: {
    maxWidth: drawerWidth,
    width: '100%',
    overflow: 'hidden',
    paddingTop: 36,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    position: 'relative', // so the close on mobile can be positioned absolute
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    borderRight: `1px solid ${colors.gray1}`,
  },
  iconWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textDecoration: 'none',
    width: '100%',
  },
  icon: {
    color: colors.gray6,
    marginTop: 24,
    marginBottom: 10,
  },
  activeIcon: {
    color: theme.palette.primary.main,
  },
  badge: {
    position: 'absolute',
    top: 26,
    right: '20%',
  },
  orangeBadge: {
    backgroundColor: colors.orange1,
  },
  redBadge: {
    backgroundColor: colors.red3,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontFamily: 'Avenir-Medium',
    color: '#B3C1D2',
  },
  labelActive: {
    color: '#4C7DE0',
  },
});

const SideBarIcon = ({ toUrl, icon: Icon, location, classes, label, showBadge }) => (
  <Link to={toUrl} className={classNames(classes.iconWrapper)} data-testid="sidebar-link">
    <Badge
      variant="dot"
      invisible={!showBadge}
      className={classNames(classes.badge)}
      classes={{ badge: classes.redBadge }}
      children=""
      data-testid={showBadge ? 'sidebar-icon-badge-active' : 'sidebar-icon-badge'}
    />
    <Icon
      className={classNames(classes.icon, {
        [classes.activeIcon]: location.pathname.includes(toUrl),
      })}
    />
    <span
      className={classNames(classes.label, {
        [classes.labelActive]: location.pathname.includes(toUrl),
      })}
    >
      {label}
    </span>
  </Link>
);

const SideBar = ({ classes, location }) => {
  const currentUser = useSelector(getCurrentUser);
  const unreadMessagesCount = useSelector(getHasUnread);
  const { rosterForTherapists } = useFlags();

  const isCoachProvider = currentUser.isCoach;
  const providerSchedulingType = currentUser.providerSchedulingType;

  const commonProps = {
    classes,
    location,
  };

  const rosterCountRunningLow = rosterForTherapists
    ? currentUser.newClientCapacity <= 2 &&
      (isCoachProvider || providerSchedulingType === 'Calendar')
    : currentUser.newClientCapacity <= 2 && isCoachProvider;

  return (
    <div className={classNames(classes.sidebar)}>
      <SideBarIcon label="Sessions" toUrl="/sessions" icon={Event} {...commonProps} />

      <SideBarIcon label="Clients" toUrl="/clients" icon={Person} {...commonProps} />

      <SideBarIcon
        label="Chat"
        toUrl="/chat"
        icon={ChatBubbleSharp}
        showBadge={unreadMessagesCount}
        {...commonProps}
      />
      <SideBarIcon label="Resources" toUrl="/resources" icon={Description} {...commonProps} />
      <SideBarIcon
        label="Settings"
        toUrl="/settings"
        icon={Settings}
        {...commonProps}
        showBadge={rosterCountRunningLow}
      />
    </div>
  );
};

export default compose(withRouter, withStyles(styles))(SideBar);
